import { cn } from '@cobliteam/ui/utils';
import { Check } from 'lucide-react';
import type { ReactNode } from 'react';

const RADIUS = 18;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

interface AvatarProgressRingProps {
  /** Overall fleet configuration percentage (0–100). */
  pct: number;
  /** Accessible label describing the progress. */
  label: string;
  /** The avatar trigger the ring wraps. */
  children: ReactNode;
  className?: string;
}

/** % at/above which the fleet is considered "halfway" through its setup (ring turns yellow). */
const MIDWAY_THRESHOLD = 50;

/**
 * Thin progress ring drawn around the topbar avatar, showing the fleet's Basic Value setup
 * percentage. Three states:
 * - below 50%: red ring + red "!" badge (needs attention);
 * - 50–99%: yellow ring + red "!" badge (in progress);
 * - 100%: Cobli-blue ring + blue verified-check badge.
 */
const AvatarProgressRing = ({ pct, label, children, className }: AvatarProgressRingProps) => {
  const value = Math.max(0, Math.min(100, Math.round(pct)));
  const isComplete = value >= 100;
  const isMidway = !isComplete && value >= MIDWAY_THRESHOLD;
  const progressState = isComplete ? 'complete' : isMidway ? 'midway' : 'low';
  const offset = CIRCUMFERENCE * (1 - value / 100);
  const ringStroke = isComplete ? 'var(--primary-9)' : isMidway ? 'var(--yellow-9)' : 'var(--red-9)';

  return (
    <div className={cn('relative inline-flex shrink-0', className)} role='img' aria-label={label} data-progress-state={progressState}>
      <svg className='pointer-events-none absolute -inset-1 size-10 -rotate-90' viewBox='0 0 40 40' aria-hidden focusable='false'>
        <circle cx='20' cy='20' r={RADIUS} fill='none' strokeWidth='2.5' style={{ stroke: 'var(--gray-4)' }} />
        <circle
          cx='20'
          cy='20'
          r={RADIUS}
          fill='none'
          strokeWidth='2.5'
          strokeLinecap='round'
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          style={{ stroke: ringStroke, transition: 'stroke-dashoffset 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
        />
      </svg>
      {children}
      {isComplete ? (
        <span
          aria-hidden
          data-testid='avatar-progress-complete'
          className='pointer-events-none absolute -right-0.5 -bottom-0.5 flex size-3.5 items-center justify-center rounded-full bg-primary text-white ring-2 ring-white'
        >
          <Check className='size-2.5' strokeWidth={3} />
        </span>
      ) : (
        <span
          aria-hidden
          data-testid='avatar-progress-pending'
          className='pointer-events-none absolute -right-0.5 -bottom-0.5 flex size-3.5 items-center justify-center rounded-full bg-danger font-bold text-[9px] text-white leading-none ring-2 ring-white'
        >
          !
        </span>
      )}
    </div>
  );
};

export default AvatarProgressRing;
